import { lazy } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { IconBundleBlueprint } from '@backstage/plugin-app-react';
import {
  AppTypeModuleBlueprint,
  type AppTypeModule,
} from '@internal/plugin-waadoo-core';
import { ConfigurationTab } from '../odooConfiguration';
import { odooIconBundle } from '../odooIcons/iconBundle';
import { OdooKeptSummary } from './OdooKeptSummary';
import { useOdooPodRoles } from './odooPods';
import { odooFrTranslation } from './translation';
import {
  WIZARD_SUBTITLE,
  WIZARD_TITLE,
} from '../odooProjectWizard/wizardTitle';
import { useOdooEnvironmentVersions } from './useOdooEnvironmentVersions';
import { useOdooPodTools } from './useOdooPodTools';
import { useOdooReadiness } from './useOdooReadiness';

/**
 * L'assistant `odoo-project`, chargé avec ses champs de formulaire à sa
 * première ouverture (il n'est pas sur le chemin de la liste de « Créer ») :
 * son propre chunk, comme avant l'étape 9d.
 */
const OdooProjectWizard = lazy(async () => {
  const module = await import('../odooProjectWizard');
  return { default: module.OdooProjectWizard };
});

/**
 * Le module du type d'application `odoo` (étape 9d du plan « plateforme
 * d'applications ») : ce qu'Odoo apporte aux vues du cœur, qui n'importent
 * rien d'ici (règle de lint de plugins/waadoo-core/.eslintrc.js).
 */
export const odooAppTypeModule: AppTypeModule = {
  appType: 'odoo',
  versions: { useVersions: useOdooEnvironmentVersions },
  branch: {
    // Le rapport du job d'init : le bandeau et la carte « Modules » de l'aperçu.
    useReadiness: useOdooReadiness,
    // L'Odoo shell, dans la carte des pods.
    usePodTools: useOdooPodTools,
    // Pas d'onglet Accès propre : celui du cœur (la carte des identifiants,
    // fonction `credentials` du descripteur, plan « généricité », étape 7).
    // L'onglet Configuration d'Odoo (étape 4 du plan « généricité »).
    ConfigurationTab,
  },
  // Le shell et le Job d'init du chart, dans les noms des pods du cœur
  // (étape 6 du plan « généricité »).
  pods: { useRoles: useOdooPodRoles },
  // « Ce qui ne change pas » du dialogue de promotion, dans les mots d'Odoo.
  promotion: { KeptSummary: OdooKeptSummary },
  // Le formulaire de `template:default/odoo-project` dans « Créer » ; son
  // titre et son sous-titre sont l'en-tête de la page sur ce formulaire.
  createWizard: {
    templatePath: 'default/odoo-project',
    title: WIZARD_TITLE,
    subtitle: WIZARD_SUBTITLE,
    Wizard: OdooProjectWizard,
  },
};

/**
 * `icon-bundle:app/odoo-icons` : les icônes d'Odoo du registre de
 * l'application (`odoo`, `odoo-shell`, `odoo-instance`), que des vues du
 * cœur lisent encore par leur nom (étape 9f).
 */
export const odooIconsExtension = IconBundleBlueprint.make({
  name: 'odoo-icons',
  params: { icons: odooIconBundle },
});

/**
 * Sur le plugin `app`, comme le registre auquel il s'attache
 * (`api:app/app-type-modules`) : `app-type-module:app/odoo`, avec ses
 * icônes (`icon-bundle:app/odoo-icons`) et sa traduction française
 * (`translation:app/odoo-fr`), sur le plugin `app` comme celles du cœur
 * (modules/icons.tsx, projectEnvironments/plugin.tsx). Enregistré
 * dans packages/app/src/App.tsx.
 */
export const odooAppModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppTypeModuleBlueprint.make({
      name: 'odoo',
      params: { module: odooAppTypeModule },
    }),
    odooIconsExtension,
    // `translation:app/odoo-fr` : les textes français du module.
    odooFrTranslation,
  ],
});
